import Phaser from "phaser";
import { TILESET_SHEET_KEY, imageLayerKey } from "../tiles";

/**
 * Render the non-interactive content authored in a Tiled map: background image
 * layers, text objects, and tile/decor objects. Shared by the game levels
 * (BaseLevelScene) and the menu screens (MenuScene) so authoring in Tiled works
 * identically everywhere. Reads the raw map JSON from the tilemap cache.
 *
 * Depth order: image layer (-10) < [world/decor 4] < text (6). Callers layer
 * their own content (player, buttons) between these as needed.
 *
 * Returns the authored text objects (in map order) so a caller can further tweak
 * them, e.g. the title screen hides its subtitle and fades it in with the logo.
 */
type RawLayer = Record<string, unknown>;
type RawObj = Record<string, unknown>;

export function renderTiledAuthoredContent(
  scene: Phaser.Scene,
  map: Phaser.Tilemaps.Tilemap,
  mapKey: string,
): Phaser.GameObjects.Text[] {
  const texts: Phaser.GameObjects.Text[] = [];
  const raw = scene.cache.tilemap.get(mapKey)?.data as { layers?: RawLayer[] } | undefined;
  if (!raw?.layers) return texts;
  const firstgid = map.tilesets[0]?.firstgid ?? 1;

  for (const layer of raw.layers) {
    if (layer.visible === false) continue;
    if (layer.type === "imagelayer") {
      renderImageLayer(scene, layer);
    } else if (layer.type === "objectgroup") {
      // The "hud" layer is screen-pinned HUD, built + token-substituted by
      // BaseLevelScene, not world content. Skip it here to avoid double-drawing.
      if (layer.name === "hud") continue;
      for (const o of (layer.objects as RawObj[]) ?? []) {
        if (o.visible === false) continue;
        const cls = (o.type as string) || (o.class as string) || (o.name as string) || "";
        if (o.text) texts.push(renderTextObject(scene, o));
        // Tile objects (a `gid`) render as decor, unless they carry a gameplay
        // class the scene handles instead. Interactive classes (e.g. `button`)
        // are handled by the caller, not here.
        else if (o.gid && (cls === "" || cls === "decor")) renderDecorObject(scene, o, firstgid);
      }
    }
  }
  return texts;
}

/** Tiled image layer: top-left origin, parallax + opacity honoured, sits behind.
 *  A `darken` custom property (0..1) multiplies the image toward black - used to
 *  push the mid background back so foreground collectibles/platforms read clearly. */
function renderImageLayer(scene: Phaser.Scene, layer: RawLayer) {
  const src = layer.image as string | undefined;
  if (!src) return;
  const key = imageLayerKey(src);
  if (!scene.textures.exists(key)) return;
  const x = ((layer.x as number) ?? 0) + ((layer.offsetx as number) ?? 0);
  const y = ((layer.y as number) ?? 0) + ((layer.offsety as number) ?? 0);
  const img = scene.add.image(x, y, key).setOrigin(0, 0).setDepth(-10);
  if (typeof layer.opacity === "number") img.setAlpha(layer.opacity);
  const darken = Phaser.Math.Clamp(layerProp(layer, "darken", 0), 0, 1);
  if (darken > 0) {
    const g = Math.round(255 * (1 - darken));
    img.setTint(Phaser.Display.Color.GetColor(g, g, g));
  }
  const px = (layer.parallaxx as number) ?? 1;
  const py = (layer.parallaxy as number) ?? 1;
  if (px !== 1 || py !== 1) img.setScrollFactor(px, py);
}

/** Read a custom property off a raw Tiled layer (array form from the JSON export). */
function layerProp<T>(layer: RawLayer, name: string, fallback: T): T {
  const props = layer.properties as Array<{ name: string; value: T }> | undefined;
  if (!Array.isArray(props)) return fallback;
  const hit = props.find((p) => p.name === name);
  return hit ? hit.value : fallback;
}

/** World text object: create it and drop it into the world at depth 6. */
function renderTextObject(scene: Phaser.Scene, o: RawObj): Phaser.GameObjects.Text {
  const txt = createTiledText(scene, o);
  txt.setDepth(6);
  return txt;
}

/**
 * Create a Phaser text from a Tiled text object: style, colour, and halign/valign
 * anchoring within the object's box. Added to the scene and positioned, but depth
 * and scrollFactor are left to the caller (world text pins to the world; HUD text
 * pins to the camera). NOTE: Tiled defaults text colour to black, invisible on the
 * dark bg - set a light Text Color in Tiled. Fonts must be web-available.
 */
export function createTiledText(scene: Phaser.Scene, o: RawObj): Phaser.GameObjects.Text {
  const t = o.text as {
    text?: string;
    color?: string;
    pixelsize?: number;
    fontfamily?: string;
    bold?: boolean;
    italic?: boolean;
    wrap?: boolean;
    halign?: "left" | "center" | "right" | "justify";
    valign?: "top" | "center" | "bottom";
  };
  const halign = t.halign ?? "left";
  const valign = t.valign ?? "top";
  const style: Phaser.Types.GameObjects.Text.TextStyle = {
    fontFamily: t.fontfamily ?? "sans-serif",
    fontSize: `${t.pixelsize ?? 16}px`,
    color: t.color ?? "#000000",
    align: halign === "justify" ? "left" : halign,
  };
  if (t.bold && t.italic) style.fontStyle = "bold italic";
  else if (t.bold) style.fontStyle = "bold";
  else if (t.italic) style.fontStyle = "italic";
  if (t.wrap && o.width) style.wordWrap = { width: o.width as number, useAdvancedWrap: true };

  const txt = scene.add.text(0, 0, t.text ?? "", style);
  // Anchor inside the Tiled text box so "center"/"right"/"bottom" place correctly.
  const ox = halign === "center" ? 0.5 : halign === "right" ? 1 : 0;
  const oy = valign === "center" ? 0.5 : valign === "bottom" ? 1 : 0;
  txt.setOrigin(ox, oy);
  const w = (o.width as number) ?? 0;
  const h = (o.height as number) ?? 0;
  txt.setPosition(((o.x as number) ?? 0) + ox * w, ((o.y as number) ?? 0) + oy * h);
  if (o.rotation) txt.setAngle(o.rotation as number);
  return txt;
}

/** Tiled tile object (has a `gid`). Tiled anchors these bottom-left. */
function renderDecorObject(scene: Phaser.Scene, o: RawObj, firstgid: number) {
  if (!scene.textures.exists(TILESET_SHEET_KEY)) return;
  const frame = ((o.gid as number) & 0x1fffffff) - firstgid; // strip flip flags
  if (frame < 0) return;
  const img = scene.add.image((o.x as number) ?? 0, (o.y as number) ?? 0, TILESET_SHEET_KEY, frame);
  img.setOrigin(0, 1).setDepth(4);
  if (o.width && o.height) img.setDisplaySize(o.width as number, o.height as number);
  if (o.rotation) img.setAngle(o.rotation as number);
}
